import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShield,
  faHeart,
  faDollarSign,
  faLaptop,
  faUsers,
  faMedkit,
  faPhone,
  faEnvelope,
  faGraduationCap,
  faComments,
  faLock,
  faExclamationTriangle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import "./css/BlogsCommon.css";
import "./css/MoreBlogs.css";
import blogsAPI from "../services/BlogsAPI";
import { formatDateOnly, formatDateForBlog } from "../utils/DateTimeUtil";
import LoadingSpinner from "../common/LoadingSpinner";

const MoreBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categoryCounts, setCategoryCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const pageSize = 6; // Number of blogs per page

  // Available categories (should match backend categories)
  const categories = [
    "All",
    "Security",
    "Romance Scams",
    "Financial Protection",
    "Tech Security",
    "Family Safety",
    "Healthcare Fraud",
    "Phone Security",
    "Email Security",
    "Digital Literacy",
    "Social Media Scams",
    "Privacy",
  ];

  // Function to get category-specific icons
  const getCategoryIcon = (category) => {
    const iconMap = {
      Security: faShield,
      "Romance Scams": faHeart,
      "Financial Protection": faDollarSign,
      "Tech Security": faLaptop,
      "Family Safety": faUsers,
      "Healthcare Fraud": faMedkit,
      "Phone Security": faPhone,
      "Email Security": faEnvelope,
      "Digital Literacy": faGraduationCap,
      "Social Media Scams": faComments,
      Privacy: faLock,
      General: faExclamationTriangle,
    };
    return iconMap[category] || faExclamationTriangle;
  };

  // Function to fetch paginated blogs
  const fetchBlogs = async (page = 0, category = "All", search = "") => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(
        `🔄 Fetching blogs - Page: ${page}, Category: ${category}, Search: ${search}`,
      );

      let paginatedData;

      if (search.trim()) {
        // If there's a search term, search across all blogs or within category
        if (category === "All") {
          paginatedData = await blogsAPI.searchBlogs(search, page, pageSize);
        } else {
          paginatedData = await blogsAPI.searchBlogsByCategory(
            search,
            category,
            page,
            pageSize,
          );
        }
      } else if (category === "All") {
        // Fetch all blogs with pagination
        paginatedData = await blogsAPI.getBlogsPaginated(page, pageSize);
      } else {
        // Fetch blogs by specific category
        paginatedData = await blogsAPI.getBlogsByCategory(
          category,
          page,
          pageSize,
        );
      }

      if (paginatedData && paginatedData.content) {
        setBlogs(paginatedData.content);
        setTotalPages(paginatedData.totalPages);
        setTotalElements(paginatedData.totalElements);
        setCurrentPage(page);

        console.log(
          `✅ Successfully loaded ${paginatedData.content.length} blogs`,
        );
        console.log(
          `📊 Total: ${paginatedData.totalElements}, Pages: ${paginatedData.totalPages}`,
        );
      } else {
        setBlogs([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("❌ Error fetching blogs:", error);
      setError(`Failed to load blogs: ${error.message}`);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch category counts for the dropdown
  const fetchCategoryCounts = async () => {
    try {
      console.log("🔄 Fetching category counts...");

      const counts = {};
      let totalAllBlogs = 0;

      // Fetch count for each specific category
      for (const category of categories.slice(1)) {
        // Skip "All" category
        try {
          const categoryData = await blogsAPI.getBlogsByCategory(
            category,
            0,
            1,
          );
          const count = categoryData.totalElements || 0;
          counts[category] = count;
          totalAllBlogs += count;

          console.log(`📊 Category "${category}": ${count} blogs`);
        } catch (error) {
          console.warn(
            `⚠️ Error fetching count for category "${category}":`,
            error,
          );
          counts[category] = 0;
        }
      }

      // Set "All" count to total of all categories
      counts["All"] = totalAllBlogs;

      setCategoryCounts(counts);
      console.log("✅ Successfully fetched category counts:", counts);
    } catch (error) {
      console.error("❌ Error fetching category counts:", error);
      // Set default counts if error
      const defaultCounts = {};
      categories.forEach((category) => (defaultCounts[category] = 0));
      setCategoryCounts(defaultCounts);
    }
  };

  // Function to handle search
  const handleSearch = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(0); // Reset to first page when searching
  };

  // Function to clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSearchInput("");
    setCurrentPage(0);
  };

  // Effect to load blogs when component mounts or when page/category/search changes
  useEffect(() => {
    fetchBlogs(currentPage, selectedCategory, searchTerm);
  }, [currentPage, selectedCategory, searchTerm]);

  // Effect to fetch category counts when component mounts
  useEffect(() => {
    fetchCategoryCounts();
  }, []);

  // Refresh category counts when blogs are updated (optional optimization)
  useEffect(() => {
    if (!isLoading && !error) {
      // Debounce the category count refresh to avoid too many API calls
      const timeout = setTimeout(() => {
        fetchCategoryCounts();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [totalElements]);

  // Handle category filter change
  const handleCategoryChange = (category) => {
    console.log(`🔍 Changing category filter to: ${category}`);
    setSelectedCategory(category);
    setCurrentPage(0); // Reset to first page when category changes
  };

  // Handle page navigation
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages && newPage !== currentPage) {
      console.log(`📄 Changing to page: ${newPage + 1}`);
      setCurrentPage(newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Handle blog card click
  const handleBlogClick = (blog) => {
    const shortTitle = blog.short_title || blog.shortTitle;
    console.log(`📰 Opening blog with short title: ${shortTitle}`);
    navigate(`/blogs/${shortTitle}`);
  };

  // Handle back to home
  const handleBackToHome = () => {
    console.log("🏠 Navigating back to home page blogs section");
    navigate("/#blogs");
    setTimeout(() => {
      const blogsSection = document.querySelector(
        '#blogs, [data-section="blogs"], .landing-page-blogs',
      );
      if (blogsSection) {
        blogsSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Calculate estimated read time based on content length
  const calculateReadTime = (content) => {
    if (!content) return "5 min read";

    // Rough estimate: 200 words per minute
    const words = content.split(" ").length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  // Strip HTML tags from summary text
  const stripHtmlTags = (html) => {
    if (!html) return "";

    // Sanitize HTML first, then extract text content
    // This prevents XSS while still extracting text from HTML
    const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });

    // Create a temporary div element to parse sanitized HTML
    const tempDiv = document.createElement("div");
    tempDiv.textContent = sanitized;

    // Extract just the text content
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  return (
    <div className="blogs-container">
      {/* Header Section */}
      <div className="blogs-header">
        <div className="header-navigation">
          <button onClick={handleBackToHome} className="back-button">
            ← Back to Home
          </button>
        </div>
        <h1 className="page-title">All KinShield Blogs</h1>
        <p className="blogs-description">
          Comprehensive protection guides and insights to help you stay safe
          online
        </p>
      </div>

      {/* Search and Filter Controls */}
      <div className="blog-controls">
        {/* Search Section */}
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search blogs by title, content, or category..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
              <button
                type="submit"
                className="search-button"
                aria-label="Search"
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>
          </form>
          {searchTerm && (
            <div className="search-info">
              <span>Showing results for: "{searchTerm}"</span>
              <div className="search-info-spacer"></div>
              <button onClick={clearSearch} className="clear-search">
                Clear search
              </button>
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="filter-section">
          <label htmlFor="category-filter" className="filter-label">
            Filter by Category:
          </label>
          <select
            id="category-filter"
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="category-filter"
          >
            {categories.map((category) => {
              const count = categoryCounts[category] || 0;
              const hasBlogs = count > 0;
              return (
                <option
                  key={category}
                  value={category}
                  style={{
                    fontWeight: hasBlogs ? "bold" : "normal",
                  }}
                >
                  {category} ({count})
                </option>
              );
            })}
          </select>

          {/* Results Summary */}
          {!isLoading && !error && (
            <p>
              Showing {blogs.length} of {totalElements} articles
              {selectedCategory !== "All" && ` in "${selectedCategory}"`}
            </p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="blog-content-wrapper">
        {isLoading ? (
          <div className="loading-state">
            <LoadingSpinner message="Loading blog posts..." />
          </div>
        ) : error ? (
          // Error State
          <div className="error-state">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button
              onClick={() =>
                fetchBlogs(currentPage, selectedCategory, searchTerm)
              }
              className="retry-button"
            >
              Try Again
            </button>
          </div>
        ) : blogs.length === 0 ? (
          // No Results State
          <div className="no-results-state">
            <h2>No blog posts found</h2>
            <p>
              {selectedCategory !== "All"
                ? `No articles found in the "${selectedCategory}" category.`
                : "No blog posts are available at the moment."}
            </p>
            {selectedCategory !== "All" && (
              <button
                onClick={() => handleCategoryChange("All")}
                className="show-all-button"
              >
                Show All Articles
              </button>
            )}
          </div>
        ) : (
          // Blog Posts Grid
          <>
            <div className="blog-list">
              <div className="blogs-grid">
                {blogs.map((blog) => (
                  <div
                    key={blog.id}
                    className="blog-card"
                    onClick={() => handleBlogClick(blog)}
                  >
                    <div className="blog-card-header">
                      <div className="blog-icon">
                        <FontAwesomeIcon
                          icon={getCategoryIcon(blog.category || "General")}
                        />
                      </div>
                      <div className="blog-meta">
                        <span className="blog-category">
                          {blog.category || "General"}
                        </span>
                        <span className="blog-read-time">
                          {blog.readTime && !blog.readTime.includes("min")
                            ? `${blog.readTime} min read`
                            : blog.readTime || calculateReadTime(blog.summary)}
                        </span>
                      </div>
                    </div>

                    <div className="blog-content">
                      <h3 className="blog-title">{blog.title}</h3>
                      <p className="blog-summary">
                        {stripHtmlTags(blog.summary) ||
                          "Click to read this informative article."}
                      </p>
                    </div>

                    <div className="blog-footer">
                      <div className="blog-author-info">
                        <span className="blog-author">
                          {blog.author || "KinShield Team"}
                        </span>
                        <span className="blog-date">
                          {formatDateForBlog(blog.published_date || blog.date)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Page {currentPage + 1} of {totalPages}
                </div>

                <div className="pagination-buttons">
                  {/* First Page */}
                  <button
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="pagination-button"
                  >
                    &laquo; First
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="pagination-button"
                  >
                    &lsaquo; Previous
                  </button>

                  {/* Page Numbers */}
                  {Array.from(
                    { length: Math.min(5, totalPages) },
                    (_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index;
                      } else if (currentPage < 2) {
                        pageNum = index;
                      } else if (currentPage > totalPages - 3) {
                        pageNum = totalPages - 5 + index;
                      } else {
                        pageNum = currentPage - 2 + index;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`pagination-button ${
                            pageNum === currentPage ? "active" : ""
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    },
                  )}

                  {/* Next Page */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="pagination-button"
                  >
                    Next &rsaquo;
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="pagination-button"
                  >
                    Last &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MoreBlogs;
